/* Occhio alla Storia — interactions for the oas-* sections */

window.__oasReady = true;

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ reveal */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const t = entry.target;
            if (t.classList.contains('oas-scroller')) {
              t.querySelectorAll('[data-oas-reveal]').forEach((el) => el.classList.add('is-in'));
            } else {
              t.classList.add('is-in');
            }
            revealObserver.unobserve(t);
          }
        },
        { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
      )
    : null;

function scanReveals(root = document) {
  root.querySelectorAll('[data-oas-reveal]:not(.is-in)').forEach((el) => {
    if (!revealObserver || reduceMotion()) {
      el.classList.add('is-in');
      return;
    }
    /* cards inside a horizontal scroller reveal together with the scroller,
       otherwise the ones peeking from the right edge would stay hidden */
    const scroller = el.closest('.oas-scroller');
    revealObserver.observe(scroller || el);
  });
}

/* --------------------------------------------------------------- scroller */
class OasScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-oas-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-oas-prev]');
    this.next = this.querySelector('[data-oas-next]');
    this.bar = this.querySelector('.oas-progress');

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.enableDrag();
    requestAnimationFrame(this.update);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.update);
  }

  step() {
    const item = this.track.firstElementChild;
    if (!item) return 300;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 18;
    return item.getBoundingClientRect().width + gap;
  }

  go(dir) {
    this.track.scrollBy({ left: dir * this.step(), behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    if (this.prev) this.prev.disabled = x <= 4;
    if (this.next) this.next.disabled = x >= max - 4;
    if (this.bar) {
      const visible = this.track.clientWidth / Math.max(this.track.scrollWidth, 1);
      const pct = max > 0 ? visible * 100 + (x / max) * (100 - visible * 100) : 100;
      this.bar.style.setProperty('--oas-progress', `${pct}%`);
    }
    this.toggleAttribute('data-static', max <= 4);
  }

  enableDrag() {
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('button, input, .oas-add, .oas-qty')) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = this.track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        this.track.classList.add('is-dragging');
      }
      if (moved) this.track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      setTimeout(() => this.track.classList.remove('is-dragging'), 40);
    });
  }
}
if (!customElements.get('oas-scroller')) customElements.define('oas-scroller', OasScroller);

/* ------------------------------------------------------------ add to cart */
let shopifyEvents = null;
async function loadEvents() {
  if (shopifyEvents) return shopifyEvents;
  try {
    shopifyEvents = await import('@shopify/events');
  } catch (e) {
    shopifyEvents = {};
  }
  return shopifyEvents;
}

const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function addToCart(button, variantId, quantity) {
  const { CartLinesUpdateEvent } = await loadEvents();

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  let deferred = null;
  if (CartLinesUpdateEvent && typeof CartLinesUpdateEvent.createPromise === 'function') {
    deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const body = { items: [{ id: Number(variantId), quantity }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const res = await fetch(`${routeRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Errore');

    const cart = await fetch(`${routeRoot()}cart.js`).then((r) => r.json());
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse
          ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          : cart,
        detail: {
          items: cart.items,
          itemCount: quantity,
          sections: data.sections,
          source: 'oas',
          didError: false,
        },
      });
    } else {
      document.querySelectorAll('[data-cart-count], .cart-bubble__text-count').forEach((el) => {
        el.textContent = cart.item_count;
      });
    }
    return true;
  } catch (error) {
    if (deferred) deferred.reject(error);
    throw error;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-oas-add]');
  if (!button || button.classList.contains('is-loading')) return;
  event.preventDefault();

  const scope = button.closest('[data-oas-buy]') || button.parentElement;
  const qtyInput = scope?.querySelector('[data-oas-qty]');
  const qty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
  const label = button.querySelector('[data-oas-add-label]');
  const original = label ? label.textContent : '';
  const icon = button.querySelector('svg');

  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
  if (icon) icon.style.display = 'none';
  const spinner = document.createElement('span');
  spinner.className = 'oas-spin';
  button.prepend(spinner);
  if (label) label.textContent = button.dataset.textAdding || 'Aggiungo…';

  let ok = false;
  try {
    ok = await addToCart(button, button.dataset.oasAdd, qty);
  } catch (e) {
    ok = false;
  }

  spinner.remove();
  if (icon) icon.style.display = '';
  button.classList.remove('is-loading');
  button.removeAttribute('aria-busy');
  button.classList.add(ok ? 'is-done' : 'is-error');
  if (label) label.textContent = ok ? button.dataset.textAdded || 'Aggiunto' : button.dataset.textError || 'Riprova';

  setTimeout(() => {
    button.classList.remove('is-done', 'is-error');
    if (label) label.textContent = original;
  }, 2600);
});

/* quantity steppers */
document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-oas-step]');
  if (!step) return;
  const box = step.closest('.oas-qty');
  const input = box?.querySelector('[data-oas-qty]');
  if (!input) return;
  const v = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.oasStep)));
  input.value = v;
  box.querySelector('[data-oas-step="-1"]').disabled = v <= 1;
  box.querySelector('[data-oas-step="1"]').disabled = v >= 99;
});

/* --------------------------------------------------------- video modal */
class OasVideoModal {
  static get el() {
    let modal = document.getElementById('oas-video-modal');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.id = 'oas-video-modal';
    modal.className = 'oas-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="oas-modal__box">
        <div class="oas-modal__bar">
          <span data-oas-modal-title></span>
          <button type="button" class="oas-icon-btn" data-oas-modal-close aria-label="Chiudi video">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>
        <div class="oas-modal__frame"><iframe title="Video" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>
      </div>`;
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.closest('[data-oas-modal-close]')) OasVideoModal.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) OasVideoModal.close();
    });
    return modal;
  }

  static open(id, title, opener) {
    const modal = OasVideoModal.el;
    OasVideoModal.opener = opener;
    modal.querySelector('[data-oas-modal-title]').textContent = title || '';
    modal.querySelector('iframe').src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('oas-lock');
    modal.querySelector('[data-oas-modal-close]').focus();
  }

  static close() {
    const modal = OasVideoModal.el;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.querySelector('iframe').src = '';
    document.documentElement.classList.remove('oas-lock');
    OasVideoModal.opener?.focus();
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-oas-video]');
  if (!trigger) return;
  event.preventDefault();
  OasVideoModal.open(trigger.dataset.oasVideo, trigger.dataset.oasTitle, trigger);
});

/* ---------------------------------------------------- YouTube channel feed */
const fmt = (n) => {
  n = Number(n) || 0;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.0', '').replace('.', ',')} Mln`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace('.0', '').replace('.', ',')}k`;
  return String(n);
};
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const decode = (s) => {
  const t = document.createElement('textarea');
  t.innerHTML = s;
  return t.value;
};

class OasYoutube extends HTMLElement {
  async connectedCallback() {
    const key = this.dataset.key;
    const channel = this.dataset.channel;
    const max = this.dataset.max || 9;
    if (!key || !channel) return;

    const cacheKey = `oas_yt_${channel}_${max}`;
    let data = null;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.ts < 15 * 60 * 1000) data = cached.data;
    } catch (e) {}

    if (!data) {
      try {
        const base = 'https://www.googleapis.com/youtube/v3';
        const [ch, vids] = await Promise.all([
          fetch(`${base}/channels?part=snippet,statistics&id=${channel}&key=${key}`).then((r) => r.json()),
          fetch(
            `${base}/search?key=${key}&channelId=${channel}&part=snippet&order=date&type=video&maxResults=${max}`
          ).then((r) => r.json()),
        ]);
        const it = ch.items && ch.items[0];
        data = {
          channel: it
            ? {
                title: decode(it.snippet.title),
                avatar: it.snippet.thumbnails.default.url,
                subs: it.statistics.subscriberCount,
                views: it.statistics.viewCount,
                videos: it.statistics.videoCount,
              }
            : null,
          videos: (vids.items || []).map((v) => ({
            id: v.id.videoId,
            title: decode(v.snippet.title),
            date: v.snippet.publishedAt,
            thumb: (v.snippet.thumbnails.high || v.snippet.thumbnails.medium).url,
          })),
        };
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
        } catch (e) {}
      } catch (e) {
        this.classList.add('is-error');
        return;
      }
    }
    this.render(data);
  }

  render(data) {
    const url = `https://www.youtube.com/channel/${this.dataset.channel}`;
    if (data.channel) {
      const head = this.querySelector('[data-yt-channel]');
      if (head) {
        head.querySelector('[data-yt-avatar]').src = data.channel.avatar;
        head.querySelector('[data-yt-name]').textContent = data.channel.title;
        head.querySelector('[data-yt-stats]').textContent = `${fmt(data.channel.subs)} iscritti · ${fmt(
          data.channel.views
        )} visualizzazioni · ${data.channel.videos} video`;
        head.hidden = false;
      }
      this.querySelectorAll('[data-yt-subscribe]').forEach((a) => (a.href = `${url}?sub_confirmation=1`));
    }
    const track = this.querySelector('[data-oas-track]');
    if (!track) return;
    const dateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    track.innerHTML = data.videos
      .map(
        (v, i) => `
        <article class="oas-yt__card" style="--oas-i:${i % 4}">
          <a href="https://www.youtube.com/watch?v=${esc(v.id)}" class="oas-yt__thumb" data-oas-video="${esc(v.id)}" data-oas-title="${esc(v.title)}">
            <img src="${esc(v.thumb)}" alt="" loading="lazy" width="480" height="360">
            <span class="oas-yt__play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg></span>
          </a>
          <div class="oas-yt__meta">
            <time datetime="${esc(v.date)}">${dateFmt.format(new Date(v.date))}</time>
            <h3><a href="https://www.youtube.com/watch?v=${esc(v.id)}" data-oas-video="${esc(v.id)}" data-oas-title="${esc(v.title)}">${esc(v.title)}</a></h3>
          </div>
        </article>`
      )
      .join('');
    this.classList.remove('is-loading');
    this.querySelector('oas-scroller')?.update?.();
  }
}
if (!customElements.get('oas-youtube')) customElements.define('oas-youtube', OasYoutube);

/* marquees only animate while on screen */
const marqueeObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
      })
    : null;
function scanMarquees(root = document) {
  if (!marqueeObserver) return;
  root.querySelectorAll('.oas-marquee').forEach((el) => marqueeObserver.observe(el));
}

/* ------------------------------------------------------------------- boot */
scanReveals();
scanMarquees();
document.addEventListener('shopify:section:load', (e) => {
  scanReveals(e.target);
  scanMarquees(e.target);
});
